<script lang="ts" setup>
import { i18n } from '@/i18n';
import MonthPicker from '@/corePagesModule/components/MonthPicker.vue';
import { storeToRefs } from 'pinia';
import { AllBusinessesForSupplierForecastStore } from '@/supplierForecastModule/pages/useAllBusinessesForSupplierForecastStore';
import { computed } from 'vue';
import SelectInput from '@/corePagesModule/SelectInput';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';

// props
const props = withDefaults(
  defineProps<{
    store: AllBusinessesForSupplierForecastStore;
    indexOfSelectInput?: number; // SelectInputの排他制御で、現在の画面で使ってるSelectInputのindexが被ってはいけないので、indexを渡す
  }>(),
  {
    indexOfSelectInput: 1,
  }
);

//変数
const t = i18n.t;
const locale = computed(() => i18n.locale);

const { state } = storeToRefs(props.store);

const modalState = computed({
  /**
   * getter
   */
  get: () => {
    return state.value.downloadForecastModalState.modalState;
  },
  /**
   * setter
   * @param value - value
   */
  set: (value) => {
    // 変数初期化
    state.value.downloadForecastModalState.startMonthPickerState = false;
    state.value.downloadForecastModalState.endSelectInputState = false;
    state.value.downloadForecastModalState.startInvalidMessageState = false;
    state.value.isOpenSelectInputType = undefined;
    state.value.downloadForecastModalState.modalState = value;
  },
});
</script>

<template>
  <InfoModal
    :modal-state="modalState"
    :back-button-label="t('もどる')"
    :next-button-label="t('ダウンロード')"
    :modal-width="500"
    :is-disabled-next-button="store.isDisabledDownloadButton"
    :is-disabled-scroll="true"
    @on-back="modalState = false"
    @on-next="store.onApplyDownload"
  >
    <template #title>{{ t('仕入先内示ダウンロード') }}</template>
    <div class="download-forecast-modal__main-content main-content">
      <div class="main-content__select-period select-period">
        <div class="select-period__label">
          {{ t('期間の選択') }}
        </div>
        <div class="select-period__content-container">
          <div class="select-period__content">
            <div class="select-period__picker-container">
              <label class="select-period__picker-label" for="start-month">{{ t('開始月') }}</label>
              <MonthPicker
                class="select-period__calendar-picker"
                :model-value="state.downloadForecastModalState.period.start"
                :prop-format="t('MonthPickerFormat')"
                :placeholder="'2024/10'"
                :locale="locale"
                :use-teleport="false"
                @update:model-value="store.onSelectedStartPeriod($event)"
                @open="store.toggleStartMonthPicker(true)"
                @closed="store.toggleStartMonthPicker(false)"
              />
            </div>
            <div class="select-period__picker-container">
              <label class="select-period__picker-label" for="end-month">{{ t('終了月') }}</label>
              <!-- 終了年月の選択はセレクトインプットから選択式にする -->
              <SelectInput
                class="select-period__end-month"
                :index="indexOfSelectInput"
                :model-value="store.getEndPeriodLabel"
                :placeholder="''"
                :label="''"
                :filter-type="'DISPLAY_YEAR_MONTH'"
                :prop-options="store.getYearMonthListForEndPeriod"
                :is-open-prop="state.downloadForecastModalState.endSelectInputState"
                @click:select-input="() => store.toggleEndSelectInput(true)"
                @close="() => store.toggleEndSelectInput(false)"
                @update:value-with-index="(_, index) => store.onSelectedEndPeriod(index)"
              />
            </div>
          </div>
          <div v-if="state.downloadForecastModalState.startInvalidMessageState" class="select-period__attention">
            {{ t('開始月の無効メッセージ') }}
          </div>
        </div>
      </div>
    </div>
  </InfoModal>
</template>

<style lang="scss" scoped>
.main-content {
  height: 100%;
  width: 100%;
  display: grid;
  grid-template-rows: repeat(2, auto);
  gap: 24px;
  align-content: space-between;
}

.select-format {
  display: grid;
  grid-template-rows: repeat(2, auto);
  gap: 16px;

  &__label {
    font-size: 16px;
    line-height: 16px;
    font-weight: 700;
    color: $textDefault;
  }

  &__content-container {
    display: grid;
    grid-template-rows: repeat(2, auto);
    gap: 8px;
    padding-left: 16px;
  }

  // TODO: 書式を選択するUI、ひとまず一律でデフォルト書式にするのでコメントアウト
  // &__radio-container {
  //   display: grid;
  //   grid-template-columns: repeat(2, auto);
  //   gap: 4px;
  //   justify-content: start;
  // }

  // &__radio-label {
  //   font-size: 14px;
  //   line-height: 20.27px;
  //   font-weight: 400;
  // }

  &__select-file-button {
    width: 160px;
    height: 40px;
    margin-left: 16px;
  }

  &__file-input {
    display: none;
  }
}

.select-period {
  display: grid;
  grid-template-rows: repeat(2, auto);
  gap: 16px;

  &__label {
    font-size: 16px;
    line-height: 16px;
    font-weight: 700;
    color: $textDefault;
  }

  &__content-container {
    display: grid;
    grid-template-rows: auto minmax(0, 16px);
    gap: 8px;
  }

  &__content {
    display: grid;
    grid-template-columns: repeat(2, auto);
    gap: 16px;
    justify-content: start;
  }

  &__attention {
    font-size: 16px;
    font-weight: 500;
    color: $semanticError;
  }

  &__picker-container {
    display: grid;
    grid-template-columns: repeat(2, auto);
    gap: 8px;
    justify-content: start;
    align-items: center;
  }

  &__picker-label {
    font-size: 14px;
    line-height: 20.27px;
    font-weight: 400;
  }

  &__calendar-picker {
    width: 100px;
    height: 32px;
    border-bottom: 1px solid $borderDefault;
    display: grid;
    place-items: center;
  }

  &__end-month :deep(.select-box__field) {
    color: $textSecondary !important; // SelectInputの文字色を変更する
  }
}
</style>

<style lang="scss">
.select-period__calendar-picker input {
  font-family: Noto Sans JP;
  width: 100%;
  font-size: 14px;
  font-weight: 400;
}
</style>
